<!--
  Smart Book — the Dialog family: scrimmed, centred, modal.
  `paper-dialog` owns the scrim, the focus trap, Escape and the backdrop; the
  projected content owns the header, the scrolling plan and the sticky footer.
-->
<paper-dialog
  [open]="quickBookService.open()"
  [title]="'shared.quick_book_dialog.smart_book' | transloco"
  [bare]="true"
  [contentOwnsScroll]="true"
  [width]="'min(680px, calc(100vw - 32px))'"
  [height]="'min(720px, calc(100vh - 64px))'"
  (closed)="close()">

  <div class="sb">

    <!-- ═══ Header ═══ -->
    <header class="sb__head">
      <span class="sb__mark" aria-hidden="true">
        <paper-icon name="zap" [size]="18" />
      </span>
      <div class="sb__titles">
        <p class="sb__title">{{ 'shared.quick_book_dialog.smart_book' | transloco }}</p>
        @if (subtitle()) {
          <p class="sb__sub">
            <button type="button" class="sb__step"
                    [disabled]="committing() || weekOffset() <= 0"
                    (click)="prevWeek()"
                    [attr.aria-label]="'shared.quick_book_dialog.previous_week' | transloco">
              <paper-icon name="chevron-left" [size]="13" />
            </button>
            <span class="sb__sub-text">{{ subtitle() }}</span>
            <button type="button" class="sb__step"
                    [disabled]="committing() || weekOffset() >= maxWeekOffset"
                    (click)="nextWeek()"
                    [attr.aria-label]="'shared.quick_book_dialog.next_week' | transloco">
              <paper-icon name="chevron-right" [size]="13" />
            </button>
          </p>
        }
      </div>
      <paper-kbd keys="B" [mod]="true" size="md" />
      <paper-button intent="ghost" size="sm" shape="icon" icon="close"
                    [ariaLabel]="'shared.quick_book_dialog.close' | transloco"
                    [disabled]="committing()"
                    (click)="close()" />
    </header>

    <!-- ═══ Body ═══ -->
    <div
      class="sb__body"
      [attr.inert]="committing() ? '' : null"
      [attr.aria-busy]="committing()">

      @if (loadError(); as error) {
        <paper-error-banner [error]="error" (retry)="retryLoad()" />
      } @else if (loading()) {
        <div class="sb__loading" role="status">
          <paper-spinner [size]="20"
                         [ariaLabel]="'shared.quick_book_dialog.loading_week' | transloco" />
          <span>{{ 'shared.quick_book_dialog.loading_your_week' | transloco }}</span>
        </div>
      } @else {

        <!-- Intro strip — what the planner does, and the one-press fill. -->
        <div class="sb__intro">
          <p class="sb__intro-copy">
            <span class="sb__intro-lead">{{ 'shared.quick_book_dialog.intro_lead' | transloco }}</span>
            @if (offeredResourcePhrase()) {
              <span class="sb__intro-detail">{{ 'shared.quick_book_dialog.intro_resources' | transloco:{ resources: offeredResourcePhrase() } }}</span>
            }
            @if (smartHintDays()) {
              <span class="sb__intro-detail">{{ 'shared.quick_book_dialog.hint_colleagues' | transloco:{ days: smartHintDays() } }}</span>
            }
          </p>
          <paper-button intent="primary" size="sm" icon="zap" (click)="smartFill()">
            {{ 'shared.quick_book_dialog.smart_fill' | transloco }}
          </paper-button>
        </div>

        <!-- Commit outcome — created / kept / per-day failures. -->
        @if (result(); as r) {
          <paper-inline-message [intent]="r.failures.length ? 'warning' : 'success'" class="sb__result">
            <span class="sb__result-line">{{ r.summary }}</span>
            @if (r.skipped) {
              <span class="sb__result-line">{{ r.skipped }}</span>
            }
            @for (failure of r.failures; track $index) {
              <span class="sb__result-line">{{ failure }}</span>
            }
          </paper-inline-message>
        }

        <!-- Fill across — one resource, every office day. -->
        @if (offeredKinds().length > 0) {
          <div class="sb__across">
            <span class="sb__across-label" id="sb-across-label">
              {{ 'shared.quick_book_dialog.fill_across' | transloco }}
            </span>
            <div class="sb__across-row" role="group" aria-labelledby="sb-across-label">
              @for (kind of offeredKinds(); track kind) {
                <paper-button size="sm"
                              [icon]="resourceIcon(kind)"
                              [variant]="acrossPressed(kind) ? 'solid' : 'outline'"
                              [tone]="acrossPressed(kind) ? 'brand' : 'neutral'"
                              [ariaPressed]="acrossPressed(kind)"
                              [disabled]="officeDayCount() === 0"
                              [ariaDescribedby]="officeDayCount() === 0 ? noOfficeReasonId : null"
                              (click)="onFillAcross(kind)">
                  {{ 'shared.quick_book_dialog.resource.' + kind | transloco }}
                </paper-button>
              }
            </div>
            <!-- One element, two jobs: the scope of the row when there IS a
                 scope, and the reason the row is disabled when there is not. -->
            @if (officeDayCount() > 0) {
              <span class="sb__across-note">
                {{ 'shared.quick_book_dialog.fill_across_note' | transloco:{ count: officeDayCount() } }}
              </span>
            } @else {
              <span class="sb__across-note sb__across-note--reason" [id]="noOfficeReasonId">
                {{ 'shared.quick_book_dialog.fill_across_reason' | transloco }}
              </span>
            }
          </div>
        }

        <!-- Day rows -->
        <ul class="sb__days"
            [attr.aria-label]="'shared.quick_book_dialog.days_of_the_week' | transloco">
          @for (day of days(); track day.date) {
            <li class="sb-day"
                [class.sb-day--weekend]="day.isWeekend"
                [class.sb-day--past]="day.isPast"
                [class.sb-day--focus]="isFocused(day)">

              <div class="sb-day__head">
                <span class="sb-day__date">
                  <span class="sb-day__dow">{{ day.dayLabel }}</span>
                  <span class="sb-day__num">{{ day.date.slice(8) }}</span>
                </span>

                <!-- A weekend was never plannable, so there is no control to
                     disable. A PAST weekday keeps its four-way — disabled,
                     with the reason linked — so the week still reads as a
                     week and the day's holdings stay visible below it. -->
                @if (day.isWeekend) {
                  <span class="sb-day__inert-label">
                    {{ 'shared.quick_book_dialog.weekend' | transloco }}
                  </span>
                } @else {
                  <sb-segmented class="sb-day__presence"
                                [options]="segmentsFor(day)"
                                [value]="day.presence"
                                [stretch]="true"
                                [ariaLabel]="'home_preview.your_week.popup.presence_label' | transloco"
                                [disabledReason]="lockReason(day)"
                                (valueChange)="setPresence(day, $event)" />
                }

                @if (day.colleaguesInOffice > 0 && !day.isPast) {
                  <span class="sb-day__colleagues"
                        [title]="'shared.quick_book_dialog.colleagues_in_title' | transloco:{ count: day.colleaguesInOffice }">
                    <paper-icon name="users" [size]="12" />
                    {{ 'shared.quick_book_dialog.colleagues_in' | transloco:{ count: day.colleaguesInOffice } }}
                  </span>
                }
              </div>

              <!-- Resources. A held resource is a fact — named from the
                   booking record and ticked. A requested one is a choice —
                   pressed, ticked, and named only once the booking exists.
                   Past days show what they held and offer nothing. -->
              @if (showsResources(day)) {
                <div class="sb-day__resources">
                  @for (kind of resourceKindsFor(day); track kind) {
                    @if (isHeld(day, kind)) {
                      <paper-tag intent="success" size="sm">
                        <paper-icon [name]="resourceIcon(kind)" [size]="12" />
                        {{ heldName(day, kind) }}
                        <paper-icon name="check" [size]="12" />
                      </paper-tag>
                    } @else if (!day.isPast) {
                      <paper-button size="xs"
                                    [icon]="resourceIcon(kind)"
                                    [variant]="wants(day, kind) ? 'solid' : 'outline'"
                                    [tone]="wants(day, kind) ? 'brand' : 'neutral'"
                                    [ariaPressed]="wants(day, kind)"
                                    (click)="onToggleResource(day, kind)">
                        {{ 'shared.quick_book_dialog.resource.' + kind | transloco }}
                        @if (wants(day, kind)) {
                          <paper-icon name="check" [size]="11" />
                        }
                      </paper-button>
                    }
                  }

                  @if (canFillDownFrom(day)) {
                    <span class="sb-day__filldown">
                      <paper-button size="xs" variant="ghost" tone="neutral" icon="arrow-down"
                                    [title]="'shared.quick_book_dialog.fill_down_title' | transloco:{ day: day.dayLabel }"
                                    (click)="onFillDown(day)">
                        {{ 'shared.quick_book_dialog.fill_down' | transloco }}
                      </paper-button>
                    </span>
                  }
                </div>
              }
            </li>
          }
        </ul>
      }
    </div>

    <!-- ═══ Footer — every figure derived from the rows above ═══ -->
    <footer class="sb__foot">
      <span
        #commitStatus
        class="sb__counts"
        [class.sb__counts--progress]="committing()"
        tabindex="-1"
        aria-live="polite"
        aria-atomic="true">
        @if (commitProgressLabel()) {
          {{ commitProgressLabel() }}
        } @else {
          {{ 'shared.quick_book_dialog.footer_counts' | transloco:{ days: officeDayCount(), bookings: bookingCount() } }}
        }
      </span>
      <paper-button intent="ghost" size="md" [disabled]="committing()" (click)="close()">
        {{ 'shared.quick_book_dialog.cancel' | transloco }}
      </paper-button>
      <paper-button intent="primary" size="md"
                    [loading]="committing()"
                    [disabled]="committing() || !hasChanges()"
                    (click)="commit()">
        @if (committing()) {
          {{ 'shared.quick_book_dialog.confirming' | transloco }}
        } @else if (bookingCount() > 0) {
          {{ 'shared.quick_book_dialog.cta_book' | transloco:{ count: bookingCount() } }}
        } @else if (presenceChangeCount() > 0) {
          {{ 'shared.quick_book_dialog.cta_update' | transloco:{ count: presenceChangeCount() } }}
        } @else {
          {{ 'shared.quick_book_dialog.cta_idle' | transloco }}
        }
      </paper-button>
    </footer>
  </div>
</paper-dialog>
